/**
 * Granted-scope inspection for the Auth0 access token.
 *
 * Auth0 puts the scopes it actually GRANTED in the token's `scope` claim —
 * a user who self-signed-up (e.g. Google SSO) gets only OIDC scopes
 * (openid/profile/email), never the app's read:/write:/delete: permissions,
 * because those come from Auth0 roles assigned by an admin.
 *
 * We only DECODE the token to decide what UI to render; verification is the
 * API's job (it re-checks every scope server-side), so a tampered token can
 * at most render an empty shell whose every request 401/403s.
 */

const b64UrlDecode = (segment: string): string => {
  const b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  // atob exists in browsers and Node >= 16 (vitest).
  return atob(b64);
};

export const getGrantedScopes = (accessToken: string): string[] => {
  try {
    const payload = JSON.parse(b64UrlDecode(accessToken.split(".")[1]));
    // Mirror the API's HasScopePermission: the union of the `scope` claim
    // (space-separated string) and the Auth0 RBAC `permissions` claim
    // (array) — with RBAC, role permissions arrive ONLY in the latter.
    const scopes =
      typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
    if (Array.isArray(payload.permissions)) {
      scopes.push(...payload.permissions.map(String));
    }
    // Keycloak (self-hosted IdP option): realm roles are named as app scopes
    // (sub-project C contract). Union them too — this mirror is UX-only, the
    // API re-authorizes every request, so no provider branching is needed.
    const realmRoles = (payload.realm_access as { roles?: unknown } | undefined)
      ?.roles;
    if (Array.isArray(realmRoles)) {
      scopes.push(...realmRoles.map(String));
    }
    return scopes;
  } catch {
    return [];
  }
};

// The app's permission scopes all use these verbs (see api HasScopePermission).
const APP_SCOPE = /^(read|write|delete):/;

export const hasAppAccess = (scopes: string[]): boolean =>
  scopes.some((s) => APP_SCOPE.test(s));

/** Returns true iff the exact scope string was granted. */
export const hasScope = (scopes: string[], scope: string): boolean =>
  scopes.includes(scope);

/**
 * Doctor portal tier: can read their own doctor record but cannot create/edit
 * doctors. Distinguishes the doctor (read:doctor, no write) from the admin
 * (read+write) and the worker (no read:doctor). Drives the "Mi cuenta"
 * relabel + skipping the doctors list. UX only — the API is the boundary.
 */
export const isDoctorTier = (scopes: string[]): boolean =>
  hasScope(scopes, "read:doctor") && !hasScope(scopes, "write:doctor");

/**
 * Admin tier: carries the read:order-all marker scope (granted only to the
 * admin role — see api ADMIN_ORDERS_SCOPE). UX only — the API is the boundary.
 */
export const isAdminTier = (scopes: string[]): boolean =>
  hasScope(scopes, "read:order-all");
